import { inject, Injectable } from '@angular/core';
import {
    CONFIG_TOKEN,
    MedicationSourceAndMedicationsDetailsViewModel,
    MedicationSourceType,
    MedicationsPerDayViewModel,
    MedicationViewModel,
    Patient,
} from '@pxl/models/common';
import { LoginService, MedicationService, PatientService, VeterinarianService } from '@pxl/models/contracts';
import {
    MedicationModel,
    MedicationService as MedicationBackendService,
    MedicationSourceAndMedicationsModel,
    PatientService as PatientBackendService,
    VeterinarianModel,
} from '@pxl/shared/generated';
import { LanguageService, RESOURCES } from '@pxl/shared/translation';
import { getFormattedShortDate, SortOrder } from '@pxl/shared/util';
import { List } from 'linq-collections';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export const MedicationSourceTypeNames = {
    [MedicationSourceType.Logbook]: RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.logbook.text,
    [MedicationSourceType.Prescription]: RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.prescription.text,
    [MedicationSourceType.UsageInstruction]: RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.usage_instruction.text,
    [MedicationSourceType.StockRegister]: RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.stock_register.text,
    [MedicationSourceType.CertificateOfVaccination]:
        RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.certificate_of_vaccination.text,
    [MedicationSourceType.CertificateOfRabies]:
        RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.certificate_of_rabies.text,
    [MedicationSourceType.LogbookLabel]: RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.logbook_label.text,
    [MedicationSourceType.UsageInstructionLabel]:
        RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.usage_instruction_label.text,
    [MedicationSourceType.Unlicensed]: RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.unlicensed.text,
};

@Injectable()
export class DefaultMedicationService implements MedicationService {
    private readonly medicationBackendService = inject(MedicationBackendService);
    private readonly patientBackendService = inject(PatientBackendService);
    private readonly loginService = inject(LoginService);
    private readonly patientService = inject(PatientService);
    private readonly veterinarianService = inject(VeterinarianService);
    private readonly languageService = inject(LanguageService);
    private readonly config = inject(CONFIG_TOKEN);

    public getMedications(filterMedicationIds: string[] = null, filterPatientIds: string[] = null): Observable<MedicationModel[]> {
        return this.medicationBackendService
            .getMedications(this.config.API_VERSION, this.loginService.getToken(), filterMedicationIds, filterPatientIds)
            .pipe(map((medications) => medications.sort(SortOrder.byTreatmentDateThenText)));
    }

    public getMedicationsByIds(medicationIds: string[]): Observable<MedicationModel[]> {
        return this.getMedications(medicationIds, null);
    }

    public getMedicationsByPatientIds(patientIds: string[]): Observable<MedicationModel[]> {
        return this.getMedications(null, patientIds);
    }

    public getActiveMedications(patientId: string): Observable<MedicationModel[]> {
        return this.patientBackendService
            .getActiveMedications(patientId, this.config.API_VERSION, this.loginService.getToken())
            .pipe(map((medications) => medications.sort(SortOrder.byTreatmentDateThenText)));
    }

    public getMedicationSourceDetails(id: string): Observable<MedicationSourceAndMedicationsDetailsViewModel> {
        const medicationSource$ = this.medicationBackendService
            .getSources([id], this.config.API_VERSION, this.loginService.getToken())
            .pipe(map((items) => items[0]));
        const veterinarian$ = (vetId: string) => this.veterinarianService.getVeterinarian(vetId);
        const patientsMap$ = async (medicationModels: MedicationModel[]) => {
            const patients = await this.patientService.getPatientsWithFilter(medicationModels.map((medication) => medication.patientId));
            return new Map<string, Patient>(patients.map((patient) => [patient.patientId, patient]));
        };

        return medicationSource$.pipe(
            switchMap((medicationSourceAndMedicationsModel: MedicationSourceAndMedicationsModel) =>
                forkJoin([
                    veterinarian$(medicationSourceAndMedicationsModel.medicationSource.document.veterinarianId),
                    patientsMap$(medicationSourceAndMedicationsModel.medications),
                ]).pipe(
                    map(([veterinarian, patientsMap]) =>
                        this.mapMedicationSourceToDetailViewModel(medicationSourceAndMedicationsModel, veterinarian, patientsMap),
                    ),
                ),
            ),
        );
    }

    private mapMedicationSourceToDetailViewModel(
        medicationSourceAndMedicationsModel: MedicationSourceAndMedicationsModel,
        veterinarian: VeterinarianModel,
        patientsMap: Map<string, Patient>,
    ): MedicationSourceAndMedicationsDetailsViewModel {
        const medicationViewModels = new List<MedicationViewModel>(
            medicationSourceAndMedicationsModel.medications.map((x) => this.mapMedicationViewModel(x)),
        )
            .groupBy((medication) => medication.medicationModel.patientId)
            .select((medicationsGroupedByPatients) => ({
                patientId: medicationsGroupedByPatients.key,
                medicationsPerDay: medicationsGroupedByPatients.value
                    .groupBy((medication) => medication.medicationModel.treatmentDateUtc as any as string)
                    .select((medicationsPerDay) => ({
                        dayString: medicationsPerDay.key,
                        medications: medicationsPerDay.value
                            .orderBy((medication) => medication.medicationModel.usageInstructions.startUtc)
                            .toArray(),
                    }))
                    .orderBy((medicationsPerDay) => medicationsPerDay.dayString)
                    .toArray(),
            }))
            .select((group) => new MedicationsPerDayViewModel(patientsMap.get(group.patientId), group.medicationsPerDay))
            .orderBy((group) => group.patient.name);

        return new MedicationSourceAndMedicationsDetailsViewModel(
            medicationSourceAndMedicationsModel.medicationSource.id,
            medicationSourceAndMedicationsModel.medicationSource.document,
            medicationSourceAndMedicationsModel.medicationSource.medicationSourceTypeId,
            medicationViewModels.toArray(),
            veterinarian,
        );
    }

    private mapMedicationViewModel(medicationModel: MedicationModel): MedicationViewModel {
        const resourceKey = RESOURCES.petsxl.ui.dashboard.dashboard_medication.medication_sources.firstseen_warning;
        const firstSeenText = this.languageService.translate(resourceKey, [
            {
                name: 'source',
                text: this.languageService.translate(
                    MedicationSourceTypeNames[medicationModel.firstSeen.medicationSourceTypeId as keyof MedicationSourceType],
                ),
            },
            {
                name: 'date',
                text: getFormattedShortDate(medicationModel.firstSeen.document.createdUtc),
            },
        ]);

        return new MedicationViewModel(medicationModel, firstSeenText);
    }
}
